/**
 * AI Visibility tab — the GEO equivalent of Rank Tracking. Shows how the
 * client's brand / domain is being mentioned across ChatGPT, Claude, Gemini
 * and Perplexity for each configured prompt.
 */
import React, { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { Sparkles, Loader2, RefreshCw, ExternalLink, Info, TrendingUp, Download } from "lucide-react";
import { rowsToCsv, downloadCsv, slugForFilename, todayStamp } from "../../lib/csv";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const PLATFORM_META = {
  chat_gpt: { label: "ChatGPT", color: "#10a37f" },
  claude: { label: "Claude", color: "#d97706" },
  gemini: { label: "Gemini", color: "#4285f4" },
  perplexity: { label: "Perplexity", color: "#20b8cd" },
};

function PlatformBadge({ platform }) {
  const m = PLATFORM_META[platform] || { label: platform, color: "#a0aec0" };
  return (
    <span
      style={{ background: `${m.color}18`, color: m.color, borderColor: `${m.color}55` }}
      className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
    >
      {m.label}
    </span>
  );
}

const GEO_STATUS = {
  cited: { label: "Cited", cls: "bg-[#e6fffa] text-[#234e52] border-[#81e6d9]", tip: "The AI found and cited your page" },
  found_not_cited: { label: "Found, not cited", cls: "bg-[#fffaf0] text-[#9c4221] border-[#fbd38d]", tip: "The AI found your page while searching but cited others" },
  not_found: { label: "Not found", cls: "bg-[#f7fafc] text-[#718096] border-[#e2e8f0]", tip: "None of your pages appeared in the AI's search" },
};

function GeoStatusChip({ r }) {
  const m = GEO_STATUS[r.geo_status];
  if (!m) return <span className="text-[11px] text-[#cbd5e0]" title="This platform doesn't share its search results">n/a</span>;
  return (
    <span title={m.tip} className={`inline-flex items-center rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${m.cls}`}>
      {m.label}
    </span>
  );
}

const hostOf = (u) => (u || "").toLowerCase().replace(/^https?:\/\//, "").split(/[/?#]/)[0].replace(/^www\./, "");

function SearchResultsList({ results, domain, rowIndex }) {
  const [open, setOpen] = useState(false);
  const d = hostOf(domain);
  return (
    <div className="mt-3" data-testid={`ai-visibility-row-${rowIndex}-search-results`}>
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold hover:text-[#4a5568]" data-testid={`ai-visibility-row-${rowIndex}-search-results-toggle`}>
        {open ? "▾" : "▸"} Pages the AI found while searching ({results.length})
      </button>
      {open && (
        <ul className="space-y-1 mt-1">
          {results.map((s, si) => {
            const h = hostOf(s.url);
            const own = d && (h === d || h.endsWith(`.${d}`));
            return (
              <li key={si} className={`text-[12px] ${own ? "font-semibold" : ""}`}>
                <a href={s.url} target="_blank" rel="noreferrer" className={`${own ? "text-[#9c4221]" : "text-[#4a5568]"} hover:underline inline-flex items-center gap-1 break-all`}>
                  <span className="font-mono-tight text-[#a0aec0]">#{si + 1}</span>
                  {s.title || s.url.replace(/\?utm_source=openai$/, "")}
                  {own && <span className="text-[9px] uppercase tracking-wider ml-1">· your site</span>}
                  <ExternalLink className="h-2.5 w-2.5" />
                </a>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function AIVisibilityTab({ client }) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [platformFilter, setPlatformFilter] = useState("all");
  const [expandedRow, setExpandedRow] = useState(null);

  const enabled = (client.ai_prompts?.length || 0) > 0 && (client.ai_platforms?.length || 0) > 0;

  const fetchSnapshots = async () => {
    try {
      const { data } = await api.get(`/clients/${client.id}/ai-visibility/snapshots?limit=12`);
      setSnapshots(data.snapshots || []);
    } catch (e) {
      toast.error("Failed to load AI Visibility history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchSnapshots(); /* eslint-disable-next-line */ }, [client.id]);

  const runNow = async () => {
    if (!enabled) {
      toast.error("Add prompts and enable at least one LLM platform first");
      return;
    }
    setRunning(true);
    try {
      const { data } = await api.post(`/clients/${client.id}/ai-visibility/run`);
      toast.success(`Snapshot done · ${data.call_count} calls · $${data.total_cost}`);
      await fetchSnapshots();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Snapshot failed");
    } finally {
      setRunning(false);
    }
  };

  const latest = snapshots[0];
  const rows = useMemo(() => {
    if (!latest) return [];
    const list = latest.results || [];
    return platformFilter === "all" ? list : list.filter((r) => r.platform === platformFilter);
  }, [latest, platformFilter]);

  const exportCsv = () => {
    const columns = [
      { header: "Prompt", accessor: (r) => r.prompt },
      { header: "Platform", accessor: (r) => PLATFORM_META[r.platform]?.label || r.platform },
      { header: "Mentioned", accessor: (r) => (r.error ? "Error" : r.mentioned ? "Y" : "N") },
      { header: "Position in answer", accessor: (r) => r.position_in_answer ?? "" },
      { header: "Source count", accessor: (r) => (r.sources || []).length },
      { header: "Source URLs", accessor: (r) => (r.sources || []).map((s) => s.url).filter(Boolean).join(" | ") },
      { header: "AI search status", accessor: (r) => GEO_STATUS[r.geo_status]?.label || "" },
      { header: "Position in AI search results", accessor: (r) => r.search_position ?? "" },
      { header: "AI search result count", accessor: (r) => r.search_result_count ?? "" },
      { header: "Model", accessor: (r) => r.model_name || "" },
    ];
    downloadCsv(
      `${slugForFilename(client.name)}-geo-${(latest.at || "").slice(0, 10) || todayStamp()}.csv`,
      rowsToCsv(latest.results, columns),
    );
  };

  const foundVsCited = useMemo(() => {
    const rs = (latest?.results || []).filter((r) => r.geo_status);
    if (!rs.length) return null;
    const found = rs.filter((r) => r.geo_status !== "not_found").length;
    const cited = rs.filter((r) => r.geo_status === "cited").length;
    return { total: rs.length, found, cited, foundPct: Math.round((100 * found) / rs.length), citedPct: Math.round((100 * cited) / rs.length) };
  }, [latest]);

  const mentionRate = useMemo(() => {
    if (!latest?.results?.length) return null;
    const n = latest.results.filter((r) => r.mentioned).length;
    return { n, total: latest.results.length, pct: Math.round((100 * n) / latest.results.length) };
  }, [latest]);

  // Trend data — one row per snapshot (oldest → newest) with per-platform
  // mention rate + a bold "All" line. `results` is the raw list of
  // {platform, mentioned, ...} entries; we bucket by platform and produce
  // 0-100 percentages. Snapshots without results are dropped defensively.
  const trend = useMemo(() => {
    if (!snapshots?.length) return { series: [], platforms: [] };
    const ordered = [...snapshots].reverse(); // API returns newest first
    const platformsSeen = new Set();
    const series = ordered.map((snap) => {
      const results = snap.results || [];
      const byPlatform = {};
      let mentioned = 0;
      for (const r of results) {
        platformsSeen.add(r.platform);
        const b = (byPlatform[r.platform] ||= { m: 0, t: 0 });
        b.t += 1;
        if (r.mentioned) { b.m += 1; mentioned += 1; }
      }
      const row = {
        label: new Date(snap.at).toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
        at: snap.at,
        all: results.length ? Math.round((100 * mentioned) / results.length) : 0,
      };
      for (const [p, b] of Object.entries(byPlatform)) {
        row[p] = b.t ? Math.round((100 * b.m) / b.t) : 0;
      }
      return row;
    });
    return { series, platforms: Array.from(platformsSeen) };
  }, [snapshots]);

  // Position-in-answer trend — average 1-based citation rank per snapshot,
  // scoped to rows where the client was actually mentioned (position != null).
  // Y-axis is INVERTED downstream (Recharts `reversed`) so #1 renders at the
  // top and larger position numbers at the bottom — matches how humans read
  // "improving" as "moving up".
  const posTrend = useMemo(() => {
    if (!snapshots?.length) return { series: [], platforms: [], maxPos: 5 };
    const ordered = [...snapshots].reverse();
    const platformsSeen = new Set();
    let maxPos = 1;
    const series = ordered.map((snap) => {
      const results = snap.results || [];
      const byPlatform = {};
      let sum = 0;
      let n = 0;
      for (const r of results) {
        if (r.position_in_answer == null) continue;    // uncited rows don't count
        platformsSeen.add(r.platform);
        const b = (byPlatform[r.platform] ||= { s: 0, n: 0 });
        b.s += r.position_in_answer;
        b.n += 1;
        sum += r.position_in_answer;
        n += 1;
        if (r.position_in_answer > maxPos) maxPos = r.position_in_answer;
      }
      const row = {
        label: new Date(snap.at).toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
        at: snap.at,
        all: n ? Number((sum / n).toFixed(1)) : null,
      };
      for (const [p, b] of Object.entries(byPlatform)) {
        row[p] = b.n ? Number((b.s / b.n).toFixed(1)) : null;
      }
      return row;
    });
    // Round maxPos up to a friendly Y-axis top (5, 10, 20…)
    const roundedMax = maxPos <= 5 ? 5 : maxPos <= 10 ? 10 : Math.ceil(maxPos / 5) * 5;
    // Drop snapshots where nothing was cited on any platform — avoids a
    // misleading "line disappears then returns" visual for stretches of
    // zero coverage.
    const filtered = series.filter((row) => row.all != null);
    return { series: filtered, platforms: Array.from(platformsSeen), maxPos: roundedMax };
  }, [snapshots]);

  return (
    <div className="space-y-5 pt-2" data-testid="ai-visibility-tab">
      {/* Header */}
      <div className="card-soft p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#4fd1c5] to-[#319795] text-white flex items-center justify-center shrink-0">
            <Sparkles className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">AI Visibility · GEO</div>
            <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">How ChatGPT, Claude, Gemini & Perplexity mention you</h3>
            <p className="text-xs text-[#718096] mt-1">
              {enabled ? (
                <>Tracking <strong>{client.ai_prompts.length}</strong> prompt{client.ai_prompts.length === 1 ? "" : "s"} across <strong>{client.ai_platforms.length}</strong> platform{client.ai_platforms.length === 1 ? "" : "s"}. Snapshots run monthly on the 1st.</>
              ) : (
                <>Not yet enabled — add prompts and pick at least one platform on the <strong>Edit Client</strong> screen.</>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
        {latest?.results?.length > 0 && (
          <button
            onClick={exportCsv}
            data-testid="ai-visibility-export-csv"
            title="Download latest AI check as CSV"
            className="btn-ghost inline-flex items-center gap-2"
          >
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        )}
        {enabled && (
          <button
            onClick={runNow}
            disabled={running || client.frozen}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="ai-visibility-run"
            className="btn-primary inline-flex items-center gap-2"
          >
            {running ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {running ? "Running…" : "Check AI now"}
          </button>
        )}
        </div>
      </div>

      {/* KPI strip */}
      {mentionRate && (
        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${foundVsCited ? "lg:grid-cols-4" : "lg:grid-cols-3"}`}>
          {foundVsCited && (
            <div className="card-soft p-4" data-testid="ai-visibility-found-vs-cited">
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">Found vs cited</div>
              <div className="font-display text-3xl mt-1 text-[#1a202c]">
                {foundVsCited.foundPct}% <span className="text-[#a0aec0] text-xl">/</span> {foundVsCited.citedPct}%
              </div>
              <div className="text-[11px] text-[#718096] mt-1">
                Found in {foundVsCited.found} of {foundVsCited.total} AI searches · cited in {foundVsCited.cited}
              </div>
            </div>
          )}
          <div className="card-soft p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">Mention rate</div>
            <div className="font-display text-3xl mt-1 text-[#1a202c]">{mentionRate.pct}%</div>
            <div className="text-[11px] text-[#718096] mt-1">{mentionRate.n} of {mentionRate.total} prompt-model pairs</div>
          </div>
          <div className="card-soft p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">Last snapshot</div>
            <div className="font-display text-3xl mt-1 text-[#1a202c]">{new Date(latest.at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}</div>
            <div className="text-[11px] text-[#718096] mt-1">{latest.call_count} calls · ${latest.total_cost}</div>
          </div>
          <div className="card-soft p-4">
            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">Total history</div>
            <div className="font-display text-3xl mt-1 text-[#1a202c]">{snapshots.length}</div>
            <div className="text-[11px] text-[#718096] mt-1">snapshots tracked</div>
          </div>
        </div>
      )}

      {/* Trend charts row — mention rate + citation position, side-by-side on lg+.
          Each needs at least 2 snapshots to render its own card. */}
      {(trend.series.length >= 2 || posTrend.series.length >= 2) && (
        <div className={`grid gap-3 ${
          trend.series.length >= 2 && posTrend.series.length >= 2
            ? "grid-cols-1 lg:grid-cols-2"
            : "grid-cols-1"
        }`}>
          {trend.series.length >= 2 && (
            <div className="card-soft p-5" data-testid="ai-visibility-trend">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-[#4fd1c5]/15 text-[#2c7a7b] flex items-center justify-center">
                <TrendingUp className="h-3.5 w-3.5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Mention rate over time</div>
                <div className="text-[12px] text-[#4a5568]">Share of prompt-model pairs that mentioned <span className="font-mono-tight text-[#2c7a7b]">{client.domain}</span></div>
              </div>
            </div>
            <div className="text-[10px] text-[#a0aec0]">Higher is better · %</div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={trend.series} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#a0aec0"
                  tick={{ fontSize: 11, fill: "#718096" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke="#a0aec0"
                  tick={{ fontSize: 11, fill: "#718096" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickFormatter={(v) => `${v}%`}
                />
                <Tooltip
                  contentStyle={{ background: "#1a202c", border: "none", borderRadius: 8, fontSize: 12, color: "white" }}
                  labelStyle={{ color: "#cbd5e0", fontSize: 11, marginBottom: 4 }}
                  formatter={(value, name) => [`${value}%`, PLATFORM_META[name]?.label || (name === "all" ? "All platforms" : name)]}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  formatter={(name) => PLATFORM_META[name]?.label || (name === "all" ? "All platforms" : name)}
                />
                <Line
                  type="monotone"
                  dataKey="all"
                  stroke="#1a202c"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#1a202c" }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                />
                {trend.platforms.map((p) => (
                  <Line
                    key={p}
                    type="monotone"
                    dataKey={p}
                    stroke={PLATFORM_META[p]?.color || "#a0aec0"}
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    dot={{ r: 2.5, fill: PLATFORM_META[p]?.color || "#a0aec0" }}
                    isAnimationActive={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
          )}
          {posTrend.series.length >= 2 && (
            <div className="card-soft p-5" data-testid="ai-visibility-position-trend">
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <div className="h-7 w-7 rounded-lg bg-[#4fd1c5]/15 text-[#2c7a7b] flex items-center justify-center">
                <TrendingUp className="h-3.5 w-3.5" strokeWidth={2.5} />
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Avg citation position over time</div>
                <div className="text-[12px] text-[#4a5568]">Where <span className="font-mono-tight text-[#2c7a7b]">{client.domain}</span> lands among cited sources · #1 = top-cited</div>
              </div>
            </div>
            <div className="text-[10px] text-[#a0aec0]">Lower is better · #1 at top</div>
          </div>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer>
              <LineChart data={posTrend.series} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#a0aec0"
                  tick={{ fontSize: 11, fill: "#718096" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e2e8f0" }}
                />
                <YAxis
                  reversed
                  domain={[1, posTrend.maxPos]}
                  allowDecimals={false}
                  stroke="#a0aec0"
                  tick={{ fontSize: 11, fill: "#718096" }}
                  tickLine={false}
                  axisLine={{ stroke: "#e2e8f0" }}
                  tickFormatter={(v) => `#${v}`}
                />
                <Tooltip
                  contentStyle={{ background: "#1a202c", border: "none", borderRadius: 8, fontSize: 12, color: "white" }}
                  labelStyle={{ color: "#cbd5e0", fontSize: 11, marginBottom: 4 }}
                  formatter={(value, name) => [`#${value}`, PLATFORM_META[name]?.label || (name === "all" ? "All platforms" : name)]}
                />
                <Legend
                  iconType="circle"
                  iconSize={8}
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  formatter={(name) => PLATFORM_META[name]?.label || (name === "all" ? "All platforms" : name)}
                />
                <Line
                  type="monotone"
                  dataKey="all"
                  stroke="#1a202c"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: "#1a202c" }}
                  activeDot={{ r: 5 }}
                  isAnimationActive={false}
                  connectNulls
                />
                {posTrend.platforms.map((p) => (
                  <Line
                    key={p}
                    type="monotone"
                    dataKey={p}
                    stroke={PLATFORM_META[p]?.color || "#a0aec0"}
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    dot={{ r: 2.5, fill: PLATFORM_META[p]?.color || "#a0aec0" }}
                    isAnimationActive={false}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
          )}
        </div>
      )}
      {trend.series.length === 1 && (
        <div className="card-soft p-4 text-[12px] text-[#a0aec0] flex items-center gap-2" data-testid="ai-visibility-trend-hint">
          <Info className="h-3.5 w-3.5" />
          One snapshot logged so far — the trend charts appear once you have at least two. The monthly cron runs on the 1st.
        </div>
      )}

      {/* Platform filter tabs */}
      {latest && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {["all", ...(latest.platforms || [])].map((p) => {
            const active = platformFilter === p;
            return (
              <button
                key={p}
                onClick={() => setPlatformFilter(p)}
                data-testid={`ai-visibility-filter-${p}`}
                className={`text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg transition-colors ${
                  active ? "bg-[#1a202c] text-white" : "bg-white border border-[#e2e8f0] text-[#4a5568] hover:text-[#1a202c]"
                }`}
              >
                {p === "all" ? "All" : (PLATFORM_META[p]?.label || p)}
              </button>
            );
          })}
        </div>
      )}

      {/* Results table */}
      {loading ? (
        <div className="card-soft p-10 flex items-center justify-center gap-3 text-sm text-[#a0aec0]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !latest ? (
        <div className="card-soft p-10 text-center text-sm text-[#a0aec0]">
          {enabled ? "No snapshots yet — click 'Check AI now' to run the first one." : "Enable AI Visibility on the Edit Client screen to get started."}
        </div>
      ) : (
        <div className="card-soft overflow-hidden">
          <div className="grid grid-cols-[1fr_100px_130px_90px_80px_60px] gap-3 px-5 py-3 border-b border-[#edf2f7] text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">
            <div>Prompt</div>
            <div>Platform</div>
            <div title="Did the AI find your page while searching, and did it cite it?">AI search</div>
            <div>Mentioned</div>
            <div>Position</div>
            <div>Sources</div>
          </div>
          {rows.map((r, i) => {
            const key = `${r.prompt}-${r.platform}-${i}`;
            const expanded = expandedRow === key;
            return (
              <div key={key} className="border-b border-[#edf2f7] last:border-b-0">
                <button
                  onClick={() => setExpandedRow(expanded ? null : key)}
                  className="w-full grid grid-cols-[1fr_100px_130px_90px_80px_60px] gap-3 px-5 py-3 items-center text-left hover:bg-[#f7fafc] transition-colors"
                  data-testid={`ai-visibility-row-${i}`}
                >
                  <div className="text-sm text-[#1a202c] truncate font-medium">{r.prompt}</div>
                  <div><PlatformBadge platform={r.platform} /></div>
                  <div data-testid={`ai-visibility-row-${i}-geo-status`}><GeoStatusChip r={r} /></div>
                  <div className="text-[11px] font-bold uppercase tracking-widest">
                    {r.mentioned ? <span className="text-[#38b2ac]">✓ Yes</span> : <span className="text-[#a0aec0]">—</span>}
                  </div>
                  <div className="text-sm font-mono-tight text-[#1a202c]">
                    {r.position_in_answer != null ? `#${r.position_in_answer}` : "—"}
                  </div>
                  <div className="text-sm font-mono-tight text-[#718096]">{r.sources?.length || 0}</div>
                </button>
                {expanded && (
                  <div className="px-5 py-4 bg-[#f7fafc] border-t border-[#edf2f7]">
                    {r.error ? (
                      <div className="text-[12px] text-[#c53030]">Error: {r.error}</div>
                    ) : (
                      <>
                        {r.text && (
                          <>
                            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1">Answer</div>
                            <div className="text-[13px] text-[#4a5568] whitespace-pre-wrap leading-relaxed max-h-48 overflow-y-auto">{r.text}</div>
                          </>
                        )}
                        {r.geo_status === "found_not_cited" && (
                          <div className="mt-3 rounded-lg border border-[#fbd38d] bg-[#fffaf0] px-3 py-2 text-[12px] text-[#7b341e]" data-testid={`ai-visibility-row-${i}-beaten-by`}>
                            The AI found your page (#{r.search_position} of {r.search_result_count} in its search) but cited {r.sources?.length ? "the pages below" : "no pages"} instead.
                          </div>
                        )}
                        {r.geo_status === "not_found" && (
                          <div className="mt-3 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-[12px] text-[#4a5568]">
                            None of your pages appeared in the AI's {r.search_result_count} search results — check the page exists, is crawlable and clearly answers this prompt.
                          </div>
                        )}
                        {r.sources?.length > 0 && (
                          <>
                            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mt-3 mb-1">
                              {r.geo_status === "found_not_cited" ? "Cited instead of you" : "Sources cited"}
                            </div>
                            <ul className="space-y-1">
                              {r.sources.map((s, si) => (
                                <li key={si} className="text-[12px]">
                                  <a href={s.url} target="_blank" rel="noreferrer" className="text-[#2c7a7b] hover:text-[#234e52] underline decoration-dotted inline-flex items-center gap-1 break-all">
                                    <span className="font-mono-tight text-[#a0aec0]">#{si + 1}</span>
                                    {s.title || s.url}
                                    <ExternalLink className="h-2.5 w-2.5" />
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </>
                        )}
                        {r.search_results?.length > 0 && <SearchResultsList results={r.search_results} domain={client.domain} rowIndex={i} />}
                        <div className="mt-3 text-[10px] text-[#a0aec0] flex items-center gap-2">
                          <Info className="h-3 w-3" />
                          {r.model_name && <span>Model: <span className="font-mono-tight">{r.model_name}</span></span>}
                          {r.cost != null && <span>· Cost: ${r.cost.toFixed(4)}</span>}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
